import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { LoginModal } from './components/LoginModal';
import { RegisterModal } from './components/RegisterModal';
import apiService from './services/ApiService';
import Home from './components/Home';
import UserProfile from './components/UserProfile';
import GarageDashboard from './components/GarageDashboard';
import VehiclePage from './components/VehiclePage';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthSessionToasts } from './components/AuthSessionToasts';
import ConditionalAppHeader from './components/ConditionalAppHeader';
import { SessionWarning } from './components/SessionWarning';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { ProtectedOrganizerRoute } from './components/ProtectedOrganizerRoute';
import OrganizerDashboard from './components/organizer/OrganizerDashboard';
import OrganizerLayout from './components/organizer/OrganizerLayout';
import CreateEventForm from './components/organizer/CreateEventForm';
import EventAdmin from './components/organizer/EventAdmin';
import EventsPage from './components/events/EventsPage';
import PublicEventPage from './components/events/PublicEventPage';
import EventRegistrationForm from './components/events/EventRegistrationForm';
import EventArchivePage from './components/events/EventArchivePage';
import EventCheckin from './components/organizer/EventCheckin';
import GateScanPage from './components/organizer/GateScanPage';
import OrganizerPaymentsPage from './components/organizer/OrganizerPaymentsPage';
import PaymentReturnPage from './components/events/PaymentReturnPage';
import SpectatorTicketsPage from './components/events/SpectatorTicketsPage';
import SpectatorPaymentReturnPage from './components/events/SpectatorPaymentReturnPage';
import { ProtectedJudgeRoute } from './components/ProtectedJudgeRoute';
import JudgeDashboard from './components/judge/JudgeDashboard';
import JudgeLayout from './components/judge/JudgeLayout';
import JudgeEventVehicles from './components/judge/JudgeEventVehicles';
import JudgeScorecard from './components/judge/JudgeScorecard';
import { AdminLayout } from './components/AdminLayout';
import { AdminDashboard } from './components/AdminDashboard';
import { UserManagement } from './components/UserManagement';
import { RegistrationCodeManagement } from './components/RegistrationCodeManagement';
import { ActivityLog } from './components/ActivityLog';
import AdminEvents from './components/AdminEvents';
import { AiSettingsManagement } from './components/AiSettingsManagement';
import { InvitationLandingPage } from './components/InvitationLandingPage';
import { RegistrationSuccessModal } from './components/RegistrationSuccessModal';
import DiscoverPage from './components/DiscoverPage';
import FavoriteBuildsPage from './components/FavoriteBuildsPage';
import ClubsDirectoryPage from './components/clubs/ClubsDirectoryPage';
import ClubProfilePage from './components/clubs/ClubProfilePage';
import ClubCreatePage from './components/clubs/ClubCreatePage';
import ClubManagePage from './components/clubs/ClubManagePage';
import BadgeDetailPage from './components/badges/BadgeDetailPage';
import AdminClubsReview from './components/AdminClubsReview';
import AdminVehicleCertifications from './components/AdminVehicleCertifications';
import AdminFeaturedBuilds from './components/AdminFeaturedBuilds';
import AdminSponsorship from './components/AdminSponsorship';
import AdminProductUpdates from './components/AdminProductUpdates';
import AdminTestimonials from './components/AdminTestimonials';
import AdminAchievements from './components/AdminAchievements';
import AdminExpansionModeration from './components/AdminExpansionModeration';
import AdminShopModeration from './components/AdminShopModeration';
import AdminPayments from './components/AdminPayments';
import AdminBadgeModeration from './components/AdminBadgeModeration';
import GlobalSearchResultsPage from './components/search/GlobalSearchResults';
import MyTicketsPage from './components/events/MyTicketsPage';
import ShopDirectoryPage from './components/shops/ShopDirectoryPage';
import ShopProfilePage from './components/shops/ShopProfilePage';
import NotificationPreferencesPage from './components/notifications/NotificationPreferencesPage';

function LoginRouteModal() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from || '/';

  const handleClose = () => {
    setOpen(false);
    navigate(returnTo === '/login' ? '/' : returnTo);
  };

  const handleLogin = () => {
    setOpen(false);
    navigate(returnTo === '/login' ? '/' : returnTo);
  };

  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <LoginModal
      isOpen={open}
      onClose={handleClose}
      onLogin={handleLogin}
      onSwitchToRegister={() => navigate('/register')}
    />
  );
}

function RegisterRouteModal({ onRegisterSuccess }: { onRegisterSuccess: () => void | Promise<void> }) {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
    navigate('/');
  };

  const handleRegister = async () => {
    setOpen(false);
    await onRegisterSuccess();
    navigate('/');
  };

  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <RegisterModal
      isOpen={open}
      onClose={handleClose}
      onRegister={handleRegister}
      onSwitchToLogin={() => navigate('/login')}
    />
  );
}

const App = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isRegistrationSuccessModalOpen, setIsRegistrationSuccessModalOpen] = useState(false);
  const [registrationSuccessData, setRegistrationSuccessData] = useState<{
    username: string;
    firstName: string;
  } | null>(null);

  const handleLogin = () => {
    setIsLoginModalOpen(false);
  };

  const handleRegister = async () => {
    setIsRegisterModalOpen(false);
    try {
      const currentUser = apiService.getCurrentUser();
      if (currentUser) {
        setRegistrationSuccessData({
          username: currentUser.username,
          firstName: currentUser.first_name,
        });
        setIsRegistrationSuccessModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to get current user data:', error);
    }
  };

  const handleSwitchToRegister = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  return (
    <ToastProvider>
      <AuthProvider>
        <AuthSessionToasts />
        <SessionWarning />
        <Router>
          <ConditionalAppHeader />
          <main id="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route
              path="/invite"
              element={
                <InvitationLandingPage
                  onRegisterClick={(code) => {
                    sessionStorage.setItem('inviteCode', code);
                    setIsRegisterModalOpen(true);
                  }}
                />
              }
            />
            <Route path="/login" element={<LoginRouteModal />} />
            <Route path="/register" element={<RegisterRouteModal onRegisterSuccess={handleRegister} />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="ai" element={<AiSettingsManagement />} />
              <Route path="codes" element={<RegistrationCodeManagement />} />
              <Route path="clubs" element={<AdminClubsReview />} />
              <Route path="certifications" element={<AdminVehicleCertifications />} />
              <Route path="featured" element={<AdminFeaturedBuilds />} />
              <Route path="sponsorship" element={<AdminSponsorship />} />
              <Route path="achievements" element={<AdminAchievements />} />
              <Route path="expansion" element={<AdminExpansionModeration />} />
              <Route path="shops" element={<AdminShopModeration />} />
              <Route path="badges" element={<AdminBadgeModeration />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="updates" element={<AdminProductUpdates />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="activity" element={<ActivityLog />} />
            </Route>
            <Route path="/garage" element={<GarageDashboard />} />
            <Route path="/favorites" element={<FavoriteBuildsPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/search" element={<GlobalSearchResultsPage />} />
            <Route path="/shops" element={<ShopDirectoryPage />} />
            <Route path="/shops/:slug" element={<ShopProfilePage />} />
            <Route path="/settings/notifications" element={<NotificationPreferencesPage />} />
            <Route path="/clubs" element={<ClubsDirectoryPage />} />
            <Route path="/clubs/new" element={<ClubCreatePage />} />
            <Route path="/clubs/:slug/manage" element={<ClubManagePage />} />
            <Route path="/clubs/:slug" element={<ClubProfilePage />} />
            <Route path="/badges/:publicId" element={<BadgeDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:slug/register" element={<EventRegistrationForm />} />
            <Route path="/events/:slug/payment/return" element={<PaymentReturnPage />} />
            <Route path="/events/:slug/tickets/return" element={<SpectatorPaymentReturnPage />} />
            <Route path="/events/:slug/tickets" element={<SpectatorTicketsPage />} />
            <Route path="/tickets" element={<MyTicketsPage />} />
            <Route path="/events/:slug/archive" element={<EventArchivePage />} />
            <Route path="/events/:slug" element={<PublicEventPage />} />
            <Route
              path="/organizer"
              element={
                <ProtectedOrganizerRoute>
                  <OrganizerLayout />
                </ProtectedOrganizerRoute>
              }
            >
              <Route index element={<OrganizerDashboard />} />
              <Route path="payments" element={<OrganizerPaymentsPage />} />
              <Route path="events/new" element={<CreateEventForm />} />
              <Route path="events/:eventId/checkin" element={<EventCheckin />} />
              <Route path="events/:eventId/gate" element={<GateScanPage />} />
              <Route path="events/:eventId" element={<EventAdmin />} />
            </Route>
            <Route
              path="/judge"
              element={
                <ProtectedJudgeRoute>
                  <JudgeLayout />
                </ProtectedJudgeRoute>
              }
            >
              <Route index element={<JudgeDashboard />} />
              <Route path="events/:eventId" element={<JudgeEventVehicles />} />
              <Route path="events/:eventId/score/:registrationId" element={<JudgeScorecard />} />
            </Route>
            <Route path="/v/:username/:slug" element={<LegacyVehicleRedirect />} />
            <Route path=":username/:vehicleSlug" element={<VehiclePage />} />
            <Route path=":username" element={<UsernameRouteWrapper />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </main>

          {isLoginModalOpen && (
            <LoginModal
              isOpen={isLoginModalOpen}
              onClose={() => setIsLoginModalOpen(false)}
              onLogin={handleLogin}
              onSwitchToRegister={handleSwitchToRegister}
            />
          )}

          {isRegisterModalOpen && (
            <RegisterModal
              isOpen={isRegisterModalOpen}
              onClose={() => setIsRegisterModalOpen(false)}
              onRegister={handleRegister}
              onSwitchToLogin={handleSwitchToLogin}
            />
          )}

          {isRegistrationSuccessModalOpen && (
            <RegistrationSuccessModal
              isOpen={isRegistrationSuccessModalOpen}
              onClose={() => {
                setIsRegistrationSuccessModalOpen(false);
                setIsRegisterModalOpen(false);
              }}
              userDetails={registrationSuccessData || undefined}
            />
          )}
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
};

function LegacyVehicleRedirect() {
  const { username, slug } = useParams<{ username: string; slug: string }>();
  const location = useLocation();
  if (!username || !slug) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/${username}/${slug}${location.search}`} replace />;
}

function UsernameRouteWrapper() {
  const { username } = useParams();
  if (!username || RESERVED_USERNAMES.includes(username.toLowerCase())) {
    return <NotFound />;
  }
  return <UserProfile />;
}

const RESERVED_USERNAMES = [
  'login',
  'register',
  'admin',
  'v',
  'garage',
  'events',
  'organizer',
  'judge',
  'discover',
  'clubs',
  'badges',
  'invite',
  'favorites',
  'shops',
  'search',
  'settings',
];

function NotFound() {
  return (
    <div className="page flex items-center justify-center">
      <div className="text-center px-4 animate-fade-in">
        <p className="text-6xl font-bold text-orange-300 mb-2">404</p>
        <h1 className="heading-section mb-3">This page isn’t here</h1>
        <p className="text-muted mb-8 max-w-sm mx-auto">
          It may have moved, or the link is old.
        </p>
        <Link to="/" className="btn-primary">Back to Bilt</Link>
      </div>
    </div>
  );
}

export default App;
